import { useMemo } from 'react'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import type { OrgDetailsTimelineWeek } from '../services/api'

interface Props {
  weeks: OrgDetailsTimelineWeek[]
  selectedFrom: string
  selectedTo: string
}

type Role = 'kjopt' | 'leaset_inn' | 'leaset_ut_naering' | 'leaset_ut_privat' | 'leaset_ut_udefinert'

const ROLES: Role[] = ['kjopt', 'leaset_inn', 'leaset_ut_naering', 'leaset_ut_privat', 'leaset_ut_udefinert']

// Translation keys, resolved with t() at render so a language switch applies.
const ROLE_LABELS = {
  kjopt: 'orgRoles.kjopt',
  leaset_inn: 'orgRoles.leasetInn',
  leaset_ut_naering: 'orgRoles.leasetUtNaering',
  leaset_ut_privat: 'orgRoles.leasetUtPrivat',
  leaset_ut_udefinert: 'orgRoles.leasetUtUdefinert',
} as const satisfies Record<Role, string>

const ROLE_COLORS: Record<Role, string> = {
  kjopt: '#0ea5e9',            // blue
  leaset_inn: '#14b8a6',       // teal
  leaset_ut_naering: '#8b5cf6',// purple
  leaset_ut_privat: '#f59e0b', // amber
  leaset_ut_udefinert: '#94a3b8', // slate
}

const SHADING = 'rgba(59, 130, 246, 0.12)'

/** Convert a Date into [iso_year, iso_week]. */
function isoWeek(d: Date): [number, number] {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  const weekNum = Math.ceil(
    (((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7
  )
  return [date.getUTCFullYear(), weekNum]
}

const weekKey = (y: number, w: number) => `${y}-W${String(w).padStart(2, '0')}`

export default function OrgDetailsTimeline({ weeks, selectedFrom, selectedTo }: Props) {
  const { t } = useTranslation('components')
  const { isDark } = useTheme()
  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  const chartData = useMemo(
    () =>
      weeks.map((w) => ({
        key: weekKey(w.iso_year, w.iso_week),
        label: t('orgDetailsTimeline.weekLabel', { year: w.iso_year, week: w.iso_week }),
        kjopt: w.kjopt,
        leaset_inn: w.leaset_inn,
        leaset_ut_naering: w.leaset_ut_naering,
        leaset_ut_privat: w.leaset_ut_privat,
        leaset_ut_udefinert: w.leaset_ut_udefinert,
      })),
    [weeks, t]
  )

  const rolesWithData = useMemo(
    () => ROLES.filter((r) => weeks.some((w) => w[r] > 0)),
    [weeks]
  )

  // Map selectedFrom/To to the closest chart keys so ReferenceArea shades them.
  const [fromKey, toKey] = useMemo(() => {
    if (!selectedFrom || !selectedTo || chartData.length === 0) return [null, null]
    const [fy, fw] = isoWeek(new Date(selectedFrom))
    const [ty, tw] = isoWeek(new Date(selectedTo))
    const fKey = weekKey(fy, fw)
    const tKey = weekKey(ty, tw)
    const keys = chartData.map((d) => d.key)
    // Clamp to existing range
    const clamp = (k: string) => {
      if (keys.includes(k)) return k
      // Find nearest by string compare (ISO keys sort lexically).
      if (k < keys[0]) return keys[0]
      if (k > keys[keys.length - 1]) return keys[keys.length - 1]
      return keys.find((x) => x >= k) ?? keys[keys.length - 1]
    }
    return [clamp(fKey), clamp(tKey)]
  }, [selectedFrom, selectedTo, chartData])

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('orgDetailsTimeline.title')}
        </h2>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {t('orgDetailsTimeline.subtitle')}
      </p>

      <ResponsiveContainer width="100%" height={150}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 10 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            interval={Math.max(0, Math.floor(chartData.length / 12) - 1)}
          />
          <YAxis
            tick={{ fill: axisColor, fontSize: 10 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: `1px solid ${gridColor}`,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11, color: axisColor }} iconType="circle" />

          {fromKey && toKey && (
            <ReferenceArea
              x1={fromKey}
              x2={toKey}
              fill={SHADING}
              stroke="none"
              ifOverflow="extendDomain"
            />
          )}

          {rolesWithData.map((role) => (
            <Line
              key={role}
              type="monotone"
              dataKey={role}
              name={t(ROLE_LABELS[role])}
              stroke={ROLE_COLORS[role]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
